/**
 * Reading a preselected engagement out of the URL.
 *
 * Authority: IMPL-ENGAGE-002 §8.3 — "deep links preselect, never dead-end".
 *
 * Two shapes carry a reference, and both mean the same thing:
 *
 * - `?e=<customer_ref>` on any customer path, alongside the existing
 *   `?focus=forwarded` that `inviteeQuestionnaireUrl` already sets.
 * - `/e/<customer_ref>`, the public resolver path a printed or forwarded mail
 *   can carry on its own.
 *
 * ┌─ A REFERENCE IS A HINT, NEVER A CREDENTIAL. ─────────────────────────────┐
 * │ Everything behind it is still gated by OTP plus a live roster row, and   │
 * │ `POST /api/v1/me/session` resolves it against the signed-in address —    │
 * │ so a reference for somebody else's engagement resolves to nothing        │
 * │ rather than to access. That is why an unparseable or unreachable one     │
 * │ falls back to the chooser instead of raising: there is nothing to        │
 * │ protect by erroring, and a dead end is the one outcome §8.3 forbids.     │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Pure and DOM-free so it can be tested under `environment: 'node'` like the
 * rest of `test/**\/*.test.ts`; the caller passes the search string in.
 */
import { CustomerRefSchema } from '@nimer/contracts/engagement'

/** The query parameter `inviteeQuestionnaireUrl` appends (§8.3). */
export const DEEP_LINK_PARAM = 'e'

/** Where the public resolver lives; `/e/<ref>` is its only shape. */
export const DEEP_LINK_PREFIX = '/e/'

/**
 * The reference this search string carries, or null.
 *
 * Null covers every failure the same way — absent, empty, repeated, malformed
 * — because the caller's response to all of them is identical: show the
 * chooser. Distinguishing them would only invite a caller to render a
 * different message for a difference that does not matter.
 */
export function refFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

  return validRef(params.get(DEEP_LINK_PARAM))
}

/** The reference a `/e/<ref>` path carries, or null. */
export function refFromPath(pathname: string): string | null {
  if (!pathname.startsWith(DEEP_LINK_PREFIX)) return null

  return validRef(pathname.slice(DEEP_LINK_PREFIX.length).replace(/\/+$/, ''))
}

/**
 * Shape-checked here rather than at the fetch, so a malformed reference never
 * becomes a request. The check is the contract's own regex — this file does
 * not restate the alphabet, which would be a second definition free to drift
 * from `mint_customer_ref()`.
 */
function validRef(candidate: string | null): string | null {
  if (candidate === null) return null

  return CustomerRefSchema.safeParse(candidate).success ? candidate : null
}

/**
 * The same URL with the reference removed.
 *
 * Called once the reference has been redeemed for a session: leaving it in
 * place means a refresh re-selects an engagement the person may since have
 * switched away from, and the switcher, not the URL, is what holds that choice
 * afterwards. Every other parameter survives — `?focus=forwarded` in
 * particular, which the questionnaire still needs after the redemption.
 */
export function withoutRef(search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.delete(DEEP_LINK_PARAM)
  const rest = params.toString()

  return rest.length === 0 ? '' : `?${rest}`
}
